import { generatePath } from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';

import { MatchParams as WorkspaceMatchParams } from '@/pages/Workspace/types';
import { MatchParams as WorkspaceTeamsMatchParams } from '@/pages/WorkspaceTeams/types';
import { addQueryParam } from '@/utils/url';

import { Routes } from '..';
import { BasetenPageEnum, BasetenPageProps } from '../types';
import { ParamsForPage } from './types';

function getFirstPath({ path }: BasetenPageProps) {
  return Array.isArray(path) ? path[0] : path;
}

function getPagePath<TPage extends BasetenPageEnum>(
  page: TPage,
  pathParams: ParamsForPage<TPage>,
): string {
  if (page === BasetenPageEnum.Home) {
    return '/';
  }

  if (page === BasetenPageEnum.WorkspaceTeams) {
    return getFirstPath(
      Routes.filter((route) => route.page === page).find((route) => {
        return getFirstPath(route).endsWith(
          `/${(pathParams as WorkspaceTeamsMatchParams).section}`,
        );
      }),
    );
  }

  if (page === BasetenPageEnum.Workspace) {
    return getFirstPath(
      Routes.filter((route) => route.page === page).find((route) => {
        return getFirstPath(route).startsWith(`/${(pathParams as WorkspaceMatchParams).section}`);
      }),
    );
  }

  const pageProps = Routes.find((route) => route.page === page);
  if (!pageProps) {
    throw new Error(`Page not found in routes ${page}`);
  }

  return getFirstPath(pageProps);
}

export function calcSwitchPath<TPage extends BasetenPageEnum>(
  page: TPage,
  pathParams: ParamsForPage<TPage>,
  queryParams: Record<string, string> = {},
): string {
  const modifiedPathParams: Record<string, string> = { ...pathParams };

  const pathTemplate = getPagePath(page, pathParams);
  const resolvedPath = generatePath(pathTemplate, modifiedPathParams);

  if (isEmpty(queryParams)) {
    return resolvedPath;
  }

  return Object.entries(queryParams).reduce(
    (accumulatedPath, [key, value]) => addQueryParam(accumulatedPath, key, value),
    resolvedPath,
  );
}
